import {
  Archive,
  FileText,
  Folder,
  Inbox,
  Send,
  ShieldAlert,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useTranslate } from "ra-core";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import type { MailboxFolder, MailboxFolderKind } from "../mailTypes";

const ICONS: Record<MailboxFolderKind, LucideIcon> = {
  inbox: Inbox,
  drafts: FileText,
  sent: Send,
  archive: Archive,
  junk: ShieldAlert,
  trash: Trash2,
};

/** The name to show: translated for the usual folders, as is for the others. */
export const useFolderLabel = () => {
  const translate = useTranslate();
  return (folder: Pick<MailboxFolder, "kind" | "name">) =>
    folder.kind ? translate(`crm.mailbox.folders.${folder.kind}`) : folder.name;
};

interface FolderListProps {
  folders: MailboxFolder[];
  selected: string;
  onSelect: (path: string) => void;
  /** A drop-down instead of a column, on a phone. */
  compact?: boolean;
}

export const FolderList = ({
  folders,
  selected,
  onSelect,
  compact,
}: FolderListProps) => {
  const translate = useTranslate();
  const label = useFolderLabel();

  if (compact) {
    return (
      <Select value={selected} onValueChange={onSelect}>
        <SelectTrigger
          className="w-full"
          aria-label={translate("crm.mailbox.folder")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {folders.map((folder) => (
            <SelectItem key={folder.path} value={folder.path}>
              {label(folder)}
              {folder.unseen > 0 ? ` (${folder.unseen})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <nav aria-label={translate("crm.mailbox.folder")}>
      <ul className="flex flex-col gap-0.5">
        {folders.map((folder) => {
          const Icon = folder.kind ? ICONS[folder.kind] : Folder;
          const isSelected = folder.path === selected;
          return (
            <li key={folder.path}>
              <button
                type="button"
                onClick={() => onSelect(folder.path)}
                aria-current={isSelected ? "page" : undefined}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer hover:bg-muted",
                  isSelected && "bg-muted font-medium",
                )}
              >
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{label(folder)}</span>
                {folder.unseen > 0 ? (
                  <span className="text-xs font-semibold text-primary">
                    {folder.unseen}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
